/**
 * @package FlorenceEGI — EGI — Home Supreme Islands
 * @author Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version 1.0.0
 * @date 2026-04-21
 * @purpose Bootstrap islands architecture: IntersectionObserver lazy-hydrates
 *          each [data-island] by fetching its JSON endpoint (Redis-cached).
 *          Target bundle <25KB gz. Zero framework. Mobile-first.
 *
 * Security contract: this entry file never uses innerHTML with data from the
 * network. All DOM is built via createElement + textContent, which is safe by
 * construction. Island implementations in ./islands/* must sanitize any
 * HTML string with DOMPurify.sanitize() before assignment.
 */

import { hydrateIsland } from "./islands/registry";

type IslandEl = HTMLElement & {
    dataset: {
        island: string;
        endpoint: string;
        state: "idle" | "loading" | "ready" | "error";
    };
};

const observer = new IntersectionObserver(
    (entries) => {
        for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target as IslandEl;
            if (el.dataset.state !== "idle") continue;
            observer.unobserve(el);
            void hydrate(el);
        }
    },
    { rootMargin: "200px 0px", threshold: 0.01 },
);

function renderError(el: IslandEl): void {
    while (el.firstChild) el.removeChild(el.firstChild);
    const p = document.createElement("p");
    p.setAttribute("role", "alert");
    p.className = "island-error";
    p.textContent = "Content unavailable. ";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.dataset.retry = "1";
    btn.textContent = "Retry";
    btn.addEventListener("click", () => {
        el.dataset.state = "idle";
        while (el.firstChild) el.removeChild(el.firstChild);
        observer.observe(el);
    });
    p.appendChild(btn);
    el.appendChild(p);
}

async function hydrate(el: IslandEl): Promise<void> {
    el.dataset.state = "loading";
    el.setAttribute("aria-busy", "true");
    try {
        const res = await fetch(el.dataset.endpoint, {
            headers: { Accept: "application/json" },
            credentials: "same-origin",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as Record<string, unknown>;
        await hydrateIsland(el.dataset.island, el, data);
        el.dataset.state = "ready";
        el.setAttribute("aria-busy", "false");
    } catch (err) {
        el.dataset.state = "error";
        el.setAttribute("aria-busy", "false");
        renderError(el);
        console.warn("[home-supreme] island failed", el.dataset.island, err);
    }
}

function boot(): void {
    document.documentElement.classList.remove("no-js");
    const islands = document.querySelectorAll<IslandEl>("[data-island]");
    islands.forEach((el) => observer.observe(el));
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
    boot();
}
